import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, map, Observable } from 'rxjs';
import { HttpService } from 'src/app/service/http/http.service';
import { NewModalConfirmationComponent } from 'src/app/shared/components/common/new-modal-confirmation/new-modal-confirmation.component';
import { SkeletonDirective } from 'src/app/shared/directives/skeleton/skeleton.directive';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { TooltipComponent } from 'src/app/shared/components/miscelanea/tooltip/tooltip.component';
import { ButtonInduxtry } from 'src/utils/models/input.interface';
import { TooltipInterface } from 'src/utils/models/miscelanea.interface';
import { DatasetItem, PaginatedResponse, SourceItem } from '../../../conexoes/models/lakehouse.model';
import { Alert, AlertEventName, AlertKind, AlertScope, AlertTargetInput, UpsertAlertPayload } from '../../models/alert.model';
import { AlertsApiService } from '../../services/alerts-api.service';

export interface AlertFormDialogData {
  alert?: Alert;
}

interface TreeDatasetRow {
  dataset: DatasetItem;
  checked: boolean;
  disabled: boolean;
  ariaLabel: string;
}

interface TreeSourceRow {
  source: SourceItem;
  open: boolean;
  checked: boolean;
  disabled: boolean;
  datasets: TreeDatasetRow[];
  showLoading: boolean;
  showEmpty: boolean;
}

const WEEKDAYS = [
  { value: 1, label: 'Segunda', short: 'Seg' },
  { value: 2, label: 'Terça', short: 'Ter' },
  { value: 3, label: 'Quarta', short: 'Qua' },
  { value: 4, label: 'Quinta', short: 'Qui' },
  { value: 5, label: 'Sexta', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
  { value: 0, label: 'Domingo', short: 'Dom' },
];

const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

const LEGACY_SERIES_MESSAGE = 'Este alerta é de série e não pode ser editado aqui por enquanto.';
const TIME_PATTERN = /^\d{2}:\d{2}(:\d{2})?$/;

@Component({
  selector: 'app-alert-form-dialog',
  templateUrl: './alert-form-dialog.component.html',
  styleUrls: ['./alert-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, MatSlideToggleModule, ButtonComponent, TooltipComponent, SkeletonDirective],
})
export class AlertFormDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AlertFormDialogComponent, boolean>);
  private readonly dialog = inject(MatDialog);
  readonly data = inject<AlertFormDialogData>(MAT_DIALOG_DATA, { optional: true });
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpService);
  private readonly alertsApi = inject(AlertsApiService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  readonly weekdays = WEEKDAYS;
  readonly selectedWeekdays = signal<number[]>([...ALL_WEEKDAYS]);
  readonly freshnessKindTooltip: TooltipInterface = {
    text: 'Notifica se não houver execução até o horário limite do período.',
    position: 'below',
  };
  readonly sources = signal<SourceItem[]>([]);
  readonly datasets = signal<DatasetItem[]>([]);
  readonly emails = signal<string[]>([]);
  readonly saving = signal(false);
  readonly deleting = signal(false);
  readonly loadingCatalog = signal(true);
  readonly catalogSkeleton = [1, 2, 3, 4];
  readonly datasetSkeleton = [1, 2, 3];
  readonly datasetsLoaded = signal(false);
  readonly emailDraft = signal('');
  readonly emailError = signal('');
  readonly errorMessage = signal('');
  readonly catalogError = signal('');
  readonly legacySeriesLabel = signal('');
  readonly checkedSourceIds = signal<Set<string>>(new Set());
  readonly checkedDatasetIds = signal<Set<string>>(new Set());
  readonly expandedSourceIds = signal<Set<string>>(new Set());
  private readonly datasetParents = signal<Record<string, string>>({});
  private datasetRequest = 0;

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    kind: ['event' as AlertKind, Validators.required],
    executionCreated: [true],
    executionCompleted: [false],
    executionFailed: [true],
    frequency: ['daily'],
    deadlineTime: ['21:00', Validators.required],
    weekday: [5],
    dayOfMonth: [1],
    enabled: [true],
  });

  readonly formSnapshot = signal(this.form.getRawValue());
  readonly treeRows = computed(() => this.buildTree());
  readonly canSave = computed(() => this.isReadyToSave());

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formSnapshot.set(this.form.getRawValue());
      if (!this.legacySeriesLabel()) this.errorMessage.set('');
    });

    const alert = this.data?.alert;
    if (alert) this.patchAlert(alert);
    this.loadSources();
  }

  toggleExpanded(sourceId: string): void {
    this.expandedSourceIds.update((current) => this.toggleSet(current, sourceId));
  }

  toggleSource(sourceId: string): void {
    if (this.legacySeriesLabel()) return;
    this.checkedSourceIds.update((current) => this.toggleSet(current, sourceId));
  }

  toggleDataset(datasetId: string): void {
    if (this.legacySeriesLabel()) return;
    const dataset = this.datasets().find((item) => item._id === datasetId);
    if (dataset && this.checkedSourceIds().has(dataset.source_id)) return;
    this.checkedDatasetIds.update((current) => this.toggleSet(current, datasetId));
  }

  setKind(kind: AlertKind): void {
    this.form.controls.kind.setValue(kind);
  }

  setFrequency(frequency: string): void {
    this.form.controls.frequency.setValue(frequency);
  }

  isWeekdaySelected(day: number): boolean {
    return this.selectedWeekdays().includes(day);
  }

  toggleWeekday(day: number): void {
    this.selectedWeekdays.update((current) => {
      if (current.includes(day)) return current.filter((item) => item !== day);
      return [...current, day];
    });
  }

  onEmailDraft(value: string): void {
    this.emailDraft.set(value);
    if (this.emailError()) this.emailError.set('');
  }

  addEmail(): void {
    const email = this.emailDraft().trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.emailError.set('Informe um e-mail válido antes de adicionar.');
      return;
    }
    this.emailError.set('');
    if (!this.emails().includes(email)) this.emails.update((current) => [...current, email]);
    this.emailDraft.set('');
  }

  removeEmail(email: string): void {
    this.emails.update((current) => current.filter((item) => item !== email));
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  askDelete(): void {
    const name = this.data?.alert?.name ?? 'este alerta';
    this.dialog.open(NewModalConfirmationComponent, {
      width: '440px',
      autoFocus: false,
      data: {
        title: 'Excluir alerta',
        description: `Tem certeza que deseja excluir o alerta "${name}"? Essa ação não pode ser desfeita.`,
        showConfirmMessage: true,
        cancelButtonName: 'Voltar',
        confirmButtonName: 'Excluir',
      },
    }).afterClosed().subscribe((confirmed) => {
      if (confirmed) this.confirmDelete();
    });
  }

  confirmDelete(): void {
    const id = this.data?.alert?._id;
    if (!id || this.deleting()) return;

    this.deleting.set(true);
    this.alertsApi.deleteAlert(id).subscribe({
      next: () => {
        this.toastr.show('Alerta excluído.', 'Sucesso', { timeOut: 3000, messageClass: 'success' });
        this.dialogRef.close(true);
      },
      error: () => {
        this.deleting.set(false);
        this.errorMessage.set('Não foi possível excluir o alerta.');
      },
    });
  }

  save(): void {
    if (this.legacySeriesLabel()) {
      this.errorMessage.set(LEGACY_SERIES_MESSAGE);
      return;
    }
    if (this.saving() || !this.canSave()) return;

    const payload = this.buildPayload();
    if (!payload) return;

    this.saving.set(true);
    const request$ = this.data?.alert
      ? this.alertsApi.updateAlert(this.data.alert._id, payload)
      : this.alertsApi.createAlert(payload);

    request$.subscribe({
      next: () => {
        this.toastr.show('Alerta salvo.', 'Sucesso', { timeOut: 3000, messageClass: 'success' });
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.saving.set(false);
        this.errorMessage.set(this.saveErrorMessage(error));
      },
    });
  }

  saveButton(): ButtonInduxtry {
    return {
      label: 'Salvar alerta',
      customStyle: 'primary',
      showLabel: true,
      loading: this.saving(),
      disabled: !this.canSave() || this.deleting(),
    };
  }

  private saveErrorMessage(error: HttpErrorResponse): string {
    const body = error.error as { message?: string | string[] } | undefined;
    const message = Array.isArray(body?.message) ? body.message[0] : body?.message;
    if (error.status === 409 && typeof message === 'string' && message.trim()) return message;
    return 'Não foi possível salvar o alerta. Revise os campos e tente de novo.';
  }

  private isReadyToSave(): boolean {
    if (this.legacySeriesLabel() || this.saving() || this.loadingCatalog()) return false;
    const value = this.formSnapshot();
    if (!this.trimmed(value.name)) return false;
    if (!this.checkedSourceIds().size && !this.checkedDatasetIds().size) return false;
    if (!this.emails().length) return false;
    if (value.kind === 'event') {
      return Boolean(value.executionCreated || value.executionCompleted || value.executionFailed);
    }
    if (!value.frequency || !TIME_PATTERN.test(this.asText(value.deadlineTime))) return false;
    if (value.frequency === 'monthly') {
      const day = Number(value.dayOfMonth);
      return Number.isInteger(day) && day >= 1 && day <= 31;
    }
    return true;
  }

  private trimmed(value: unknown): string {
    return this.asText(value).trim();
  }

  private asText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private patchAlert(alert: Alert): void {
    if (this.isLegacySeries(alert)) {
      this.legacySeriesLabel.set(`Série: ${this.seriesNames(alert)}`);
      this.errorMessage.set(LEGACY_SERIES_MESSAGE);
    }
    this.restoreTargets(alert);
    this.emails.set(alert.recipients.map((recipient) => recipient.email));
    this.form.patchValue({
      name: alert.name ?? '',
      kind: alert.kind,
      executionCreated: alert.events?.includes('execution_created') ?? false,
      executionCompleted: alert.events?.includes('execution_completed') ?? false,
      executionFailed: alert.events?.includes('execution_failed') ?? false,
      frequency: alert.freshness?.frequency ?? 'daily',
      deadlineTime: alert.freshness?.deadline_time ?? '21:00',
      weekday: alert.freshness?.weekday ?? 5,
      dayOfMonth: alert.freshness?.day_of_month ?? 1,
      enabled: alert.enabled !== false,
    });
    this.selectedWeekdays.set(this.weekdaysFromAlert(alert));
    this.formSnapshot.set(this.form.getRawValue());
  }

  private weekdaysFromAlert(alert: Alert): number[] {
    const days = this.sortedWeekdays(alert.freshness?.weekdays ?? []);
    if (!days.length || days.length === 7) return [...ALL_WEEKDAYS];
    return days;
  }

  private isLegacySeries(alert: Alert): boolean {
    if (alert.scope === 'series') return true;
    return (alert.targets ?? []).some((target) => target.level === 'series');
  }

  private seriesNames(alert: Alert): string {
    const names = (alert.targets ?? []).map((target) => target.name).filter(Boolean);
    if (names.length) return names.join(', ');
    return alert.scope_name || 'nome não informado';
  }

  private restoreTargets(alert: Alert): void {
    const sources = new Set<string>();
    const datasets = new Set<string>();
    const expanded = new Set<string>();
    const parents: Record<string, string> = {};
    const targets = alert.targets ?? [];

    if (targets.length) {
      for (const target of targets) {
        const level = target.level ?? (alert.scope === 'mixed' ? undefined : alert.scope);
        if (level === 'source') {
          sources.add(target.id);
          expanded.add(target.id);
        } else if (level === 'dataset') {
          datasets.add(target.id);
          if (target.source_id) {
            parents[target.id] = target.source_id;
            expanded.add(target.source_id);
          }
        }
      }
    } else if (alert.scope === 'source') {
      this.idsOf(alert).forEach((id) => {
        sources.add(id);
        expanded.add(id);
      });
    } else if (alert.scope === 'dataset') {
      this.idsOf(alert).forEach((id) => datasets.add(id));
    }

    this.checkedSourceIds.set(sources);
    this.checkedDatasetIds.set(datasets);
    this.expandedSourceIds.set(expanded);
    this.datasetParents.set(parents);
  }

  private idsOf(alert: Alert): string[] {
    if (alert.scope_ids?.length) return alert.scope_ids;
    return alert.scope_id ? [alert.scope_id] : [];
  }

  private buildPayload(): UpsertAlertPayload | null {
    const value = this.form.getRawValue();
    const targets = this.selectedTargets();
    const name = this.trimmed(value.name);
    if (!name) {
      this.errorMessage.set('Dê um nome para reconhecer este alerta na lista.');
      return null;
    }
    if (!targets.length) {
      this.errorMessage.set('Selecione ao menos uma fonte ou um dataset.');
      return null;
    }
    if (!this.emails().length) {
      this.errorMessage.set('Inclua ao menos um e-mail. O aviso só sai por e-mail.');
      return null;
    }

    const scope = this.scopeFor(targets);
    const shared = {
      name,
      scope,
      scope_ids: targets.map((target) => target.id),
      targets,
      emails: this.emails(),
      enabled: value.enabled,
    };

    if (value.kind === 'event') {
      const events = this.selectedEvents(value);
      if (!events.length) {
        this.errorMessage.set('Escolha ao menos um evento para disparar o aviso.');
        return null;
      }
      return { ...shared, kind: 'event', events };
    }

    const day = Number(value.dayOfMonth);
    if (value.frequency === 'monthly' && (!Number.isInteger(day) || day < 1 || day > 31)) {
      this.errorMessage.set('O dia do mês precisa estar entre 1 e 31.');
      return null;
    }
    const deadlineTime = this.asText(value.deadlineTime);
    if (!TIME_PATTERN.test(deadlineTime)) {
      this.errorMessage.set('Informe um horário válido.');
      return null;
    }

    return {
      ...shared,
      kind: 'freshness',
      freshness: {
        frequency: value.frequency as 'daily' | 'weekly' | 'monthly',
        deadline_time: deadlineTime,
        weekday: value.frequency === 'weekly' ? Number(value.weekday) : undefined,
        weekdays: value.frequency === 'daily' ? this.sortedWeekdays(this.selectedWeekdays()) : undefined,
        day_of_month: value.frequency === 'monthly' ? day : undefined,
      },
    };
  }

  private sortedWeekdays(days: number[]): number[] {
    return [...new Set(days.filter((day) => day >= 0 && day <= 6))].sort((left, right) => left - right);
  }

  private selectedTargets(): AlertTargetInput[] {
    const covered = this.checkedSourceIds();
    const parents = this.datasetParents();
    const datasetById = new Map(this.datasets().map((dataset) => [dataset._id, dataset]));
    // Fonte marcada cobre os filhos; o alvo gravado é a fonte, não cada dataset.
    const datasetIds = [...this.checkedDatasetIds()].filter((id) => {
      const parentId = datasetById.get(id)?.source_id ?? parents[id];
      return !parentId || !covered.has(parentId);
    });

    return [
      ...[...covered].map((id) => ({ level: 'source' as const, id })),
      ...datasetIds.map((id) => ({ level: 'dataset' as const, id })),
    ];
  }

  private scopeFor(targets: AlertTargetInput[]): AlertScope {
    const levels = new Set(targets.map((target) => target.level));
    if (levels.size > 1) return 'mixed';
    if (levels.has('dataset')) return 'dataset';
    return 'source';
  }

  private selectedEvents(value: {
    executionCreated: boolean;
    executionCompleted: boolean;
    executionFailed: boolean;
  }): AlertEventName[] {
    const events: AlertEventName[] = [];
    if (value.executionCreated) events.push('execution_created');
    if (value.executionCompleted) events.push('execution_completed');
    if (value.executionFailed) events.push('execution_failed');
    return events;
  }

  private loadSources(): void {
    const projectId = sessionStorage.getItem('projectId');
    const filters = projectId ? [{ param: 'project_id', value: projectId }] : [];
    this.loadingCatalog.set(true);
    this.catalogError.set('');
    this.listAll<SourceItem>('sources', filters).subscribe({
      next: (rows) => {
        this.sources.set(rows);
        this.loadDatasets();
      },
      error: () => {
        this.loadingCatalog.set(false);
        this.catalogError.set('Não foi possível carregar as fontes.');
      },
    });
  }

  private loadDatasets(): void {
    const request = ++this.datasetRequest;
    const sourceIds = this.sources().map((source) => source._id);
    if (!sourceIds.length) {
      this.datasets.set([]);
      this.datasetsLoaded.set(true);
      this.loadingCatalog.set(false);
      return;
    }

    this.loadingCatalog.set(true);
    forkJoin(
      sourceIds.map((sourceId) => this.listAll<DatasetItem>('datasets', [{ param: 'source_id', value: sourceId }]))
    ).subscribe({
      next: (groups) => {
        if (request !== this.datasetRequest) return;
        const rows = groups.flat();
        this.datasets.set(rows);
        this.expandOwners(rows);
        this.datasetsLoaded.set(true);
        this.loadingCatalog.set(false);
      },
      error: () => {
        if (request !== this.datasetRequest) return;
        this.datasetsLoaded.set(true);
        this.loadingCatalog.set(false);
        this.catalogError.set('Não foi possível carregar os datasets.');
      },
    });
  }

  private expandOwners(rows: DatasetItem[]): void {
    const explicit = this.checkedDatasetIds();
    if (!explicit.size) return;
    this.expandedSourceIds.update((current) => {
      const next = new Set(current);
      rows.forEach((dataset) => {
        if (explicit.has(dataset._id)) next.add(dataset.source_id);
      });
      return next;
    });
  }

  private listAll<T>(endpoint: string, filters: Array<{ param: string; value: string }>): Observable<T[]> {
    const request$ = filters.length
      ? this.http.genericGetParams<PaginatedResponse<T>>(endpoint, filters)
      : this.http.genericGet<PaginatedResponse<T>>(endpoint);

    return request$.pipe(map((response) => response.rows ?? []));
  }

  private buildTree(): TreeSourceRow[] {
    const expanded = this.expandedSourceIds();
    const checkedSources = this.checkedSourceIds();
    const checkedDatasets = this.checkedDatasetIds();
    const locked = this.legacySeriesLabel().length > 0;
    const loaded = this.datasetsLoaded();
    const failed = Boolean(this.catalogError());
    const bySource = new Map<string, DatasetItem[]>();

    for (const dataset of this.datasets()) {
      const group = bySource.get(dataset.source_id) ?? [];
      group.push(dataset);
      bySource.set(dataset.source_id, group);
    }

    return this.sources().map((source) => {
      const children = bySource.get(source._id) ?? [];
      const sourceChecked = checkedSources.has(source._id);
      const open = expanded.has(source._id);

      return {
        source,
        open,
        checked: sourceChecked,
        disabled: locked,
        showLoading: open && !loaded,
        showEmpty: open && loaded && children.length === 0 && !failed,
        datasets: open
          ? children.map((dataset) => ({
              dataset,
              checked: sourceChecked || checkedDatasets.has(dataset._id),
              disabled: locked || sourceChecked,
              ariaLabel: sourceChecked
                ? `Dataset ${dataset.name}, coberto pela fonte`
                : `Dataset ${dataset.name}`,
            }))
          : [],
      };
    });
  }

  private toggleSet(current: Set<string>, id: string): Set<string> {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }
}
